import { useMemo } from 'react';
import { useIntl } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import {
  AnchorNavigation,
  AnchorNavigationProps,
} from '@designsystem/stories/04-organisms/AnchorNavigation';
import {
  traverseBlocks,
  VoltoBlock,
} from '@fhnw/helpers/Blocks/traverseBlocks';

const View = ({
  content,
}: {
  content: Content;
  isEdit?: boolean;
  isViewOrEdit?: boolean;
}) => {
  const intl = useIntl();

  // Derive anchor nav items synchronously from content.blocks so SSR and CSR
  // produce identical output — eliminates layout shift from useEffect DOM query.
  const items = useMemo<AnchorNavigationProps['items']>(() => {
    const blocks = (content?.blocks as Record<string, VoltoBlock>) || {};
    const layoutItems = (content?.blocks_layout?.items as string[]) || [];
    const result: AnchorNavigationProps['items'] = [];
    const resultIndexByAnchor = new Map<
      string,
      { index: number; hasNavTitle: boolean }
    >();

    const formatBlockLabel = (
      value: string | { id: string; defaultMessage?: string } | undefined,
    ) =>
      typeof value === 'object' && value?.id
        ? intl.formatMessage(value)
        : (value as string) || undefined;

    // Viewport visibility composes along the container chain: a block is only
    // shown on a viewport if itself and every ancestor group is shown there
    // (flags default to true, matching the group block's View.jsx).
    const shownOnDesktop = (chain: VoltoBlock[]) =>
      chain.every((b) => b.showOnDesktop !== false);
    const shownOnMobile = (chain: VoltoBlock[]) =>
      chain.every((b) => b.showOnMobile !== false);

    traverseBlocks(
      blocks,
      layoutItems,
      (id, block, ancestors) => {
        const rawNavTitle = block.block_nav_title;
        const navTitle = formatBlockLabel(rawNavTitle);

        // Fallback: blocks with a block_title (renders as h2) but no block_nav_title.
        // block_followup blocks use h3 so they are excluded (same as DOM selector).
        const rawBlockTitle =
          !navTitle && !block.block_followup ? block.block_title : undefined;
        const blockTitle = formatBlockLabel(rawBlockTitle);
        const headingTitle =
          !navTitle &&
          !blockTitle &&
          block['@type'] === 'heading' &&
          (!block.tag || block.tag === 'h2')
            ? block.heading
            : undefined;

        const label = navTitle || blockTitle || headingTitle;
        if (label) {
          // Anchor items of viewport-restricted targets carry the group
          // block's visibility class so CSS hides them on the same viewport
          // where the target is display:none'd (#1634, note_802703).
          const chain = [...ancestors, block];
          const onDesktop = shownOnDesktop(chain);
          const onMobile = shownOnMobile(chain);
          const className =
            onDesktop && !onMobile
              ? 'show_on_desktop'
              : onMobile && !onDesktop
                ? 'show_on_mobile'
                : undefined;

          const existing = resultIndexByAnchor.get(id);
          if (existing) {
            if (navTitle && !existing.hasNavTitle) {
              result[existing.index] = { label, anchor: id, className };
              resultIndexByAnchor.set(id, {
                ...existing,
                hasNavTitle: true,
              });
            }
            return;
          }

          resultIndexByAnchor.set(id, {
            index: result.length,
            hasNavTitle: Boolean(navTitle),
          });
          result?.push({ label, anchor: id, className });
        }
      },
      // Prune the subtree of blocks that render nothing on any viewport, so they
      // and everything inside them stay out of the anchor navigation:
      //  - block_hide renders no markup at all.
      //  - a block whose own + inherited showOnDesktop/showOnMobile flags leave
      //    no viewport (e.g. `hide_on_all_viewports`, or a mobile-only group
      //    inside a desktop-only group) is display:none everywhere. Desktop-only
      //    / mobile-only chains still render on one viewport, so they stay.
      {
        skip: (_id, block, ancestors) => {
          if (block.block_hide === true) return true;
          const chain = [...ancestors, block];
          return !shownOnDesktop(chain) && !shownOnMobile(chain);
        },
      },
    );
    return result;
  }, [content?.blocks, content?.blocks_layout, intl]);

  if (!items?.length) {
    return null;
  }

  return <AnchorNavigation items={items} />;
};

export default View;
